import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Button,
  TextField,
  InputAdornment,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
  Alert,
  Stack,
  Pagination
} from '@mui/material';
import {
  Search as SearchIcon,
  Visibility as VisibilityIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  FilterList as FilterListIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  HourglassEmpty as HourglassEmptyIcon,
  Cancel as CancelIcon
} from '@mui/icons-material';
import whatsappService from '../services/whatsappService';
import { format } from 'date-fns';
import { ar } from 'date-fns/locale';
import { usePermissions } from '../hooks/usePermissions';

interface Campaign {
  _id: string;
  name: string;
  description?: string;
  templateId?: string;
  templateName?: string;
  messageType: string;
  totalRecipients: number;
  successCount: number;
  failedCount: number;
  pendingCount: number;
  status: 'pending' | 'processing' | 'completed' | 'failed' | 'cancelled';
  progressPercentage: number;
  scheduledAt?: string;
  startedAt?: string;
  completedAt?: string;
  campaignType: string;
  providerSlug: string;
  metadata?: {
    campaignId?: string;
    campaignName?: string;
  };
  createdAt: string;
  updatedAt: string;
}

const WhatsAppCampaignsPage: React.FC = () => {
  const { hasPermission } = usePermissions();
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedCampaign, setSelectedCampaign] = useState<Campaign | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [alert, setAlert] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  
  // التحقق من الصلاحيات
  const canViewDetails = hasPermission('marketing', 'whatsapp-campaigns', 'view');
  const canCancel = hasPermission('marketing', 'whatsapp-campaigns', 'cancel');

  useEffect(() => {
    loadCampaigns();
  }, [page, statusFilter, typeFilter]);

  const loadCampaigns = async () => {
    try {
      setLoading(true);
      const result = await whatsappService.getBulkBatches({
        page,
        limit: 10,
        status: statusFilter !== 'all' ? statusFilter : undefined,
        campaignType: typeFilter !== 'all' ? typeFilter : undefined
      });
      
      setCampaigns(result.batches || []);
      setTotalPages(result.pagination?.pages || 1);
    } catch (error: any) {
      showAlert('error', error.message || 'فشل تحميل الحملات');
    } finally {
      setLoading(false);
    }
  };

  const showAlert = (type: 'success' | 'error', message: string) => {
    setAlert({ type, message });
    setTimeout(() => setAlert(null), 5000);
  };

  const handleViewDetails = (campaign: Campaign) => {
    setSelectedCampaign(campaign);
    setDetailsOpen(true);
  };

  const handleCancelCampaign = async (campaignId: string) => {
    if (!window.confirm('هل أنت متأكد من إلغاء هذه الحملة؟')) return;

    try {
      await whatsappService.cancelBulkBatch(campaignId);
      showAlert('success', 'تم إلغاء الحملة بنجاح');
      loadCampaigns();
    } catch (error: any) {
      showAlert('error', error.message || 'فشل إلغاء الحملة');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'processing':
        return 'info';
      case 'failed':
        return 'error';
      case 'cancelled':
        return 'default';
      default:
        return 'warning';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircleIcon fontSize="small" />;
      case 'processing':
        return <HourglassEmptyIcon fontSize="small" />;
      case 'failed':
        return <ErrorIcon fontSize="small" />;
      case 'cancelled':
        return <CancelIcon fontSize="small" />;
      default:
        return <ScheduleIcon fontSize="small" />;
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'completed':
        return 'مكتملة';
      case 'processing':
        return 'قيد الإرسال';
      case 'failed':
        return 'فشلت';
      case 'cancelled':
        return 'ملغاة';
      case 'pending':
        return 'معلقة';
      default:
        return status;
    }
  };

  const getCampaignTypeText = (type: string) => {
    switch (type) {
      case 'marketing':
        return 'تسويقية';
      case 'transactional':
        return 'معاملات';
      case 'notification':
        return 'إشعارات';
      default:
        return type;
    }
  };

  const filteredCampaigns = campaigns.filter(campaign =>
    campaign.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    campaign.templateName?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    try {
      return format(new Date(dateString), 'dd/MM/yyyy HH:mm', { locale: ar });
    } catch {
      return '-';
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4" component="h1">
          قائمة الحملات المرسلة
        </Typography>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={loadCampaigns}
          disabled={loading}
        >
          تحديث
        </Button>
      </Box>

      {/* Alert */}
      {alert && (
        <Alert severity={alert.type} sx={{ mb: 3 }} onClose={() => setAlert(null)}>
          {alert.message}
        </Alert>
      )}

      {/* Filters */}
      <Card sx={{ mb: 3, p: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              placeholder="بحث بالاسم أو القالب..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>الحالة</InputLabel>
              <Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                label="الحالة"
              >
                <MenuItem value="all">الكل</MenuItem>
                <MenuItem value="pending">معلقة</MenuItem>
                <MenuItem value="processing">قيد الإرسال</MenuItem>
                <MenuItem value="completed">مكتملة</MenuItem>
                <MenuItem value="failed">فشلت</MenuItem>
                <MenuItem value="cancelled">ملغاة</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>نوع الحملة</InputLabel>
              <Select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                label="نوع الحملة"
              >
                <MenuItem value="all">الكل</MenuItem>
                <MenuItem value="marketing">تسويقية</MenuItem>
                <MenuItem value="transactional">معاملات</MenuItem>
                <MenuItem value="notification">إشعارات</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={2}>
            <Typography variant="body2" color="text.secondary">
              إجمالي: {filteredCampaigns.length} حملة
            </Typography>
          </Grid>
        </Grid>
      </Card>

      {/* Table */}
      <Card>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>اسم الحملة</TableCell>
                <TableCell>رقم الحملة (المزود)</TableCell>
                <TableCell>نوع الحملة</TableCell>
                <TableCell>القالب</TableCell>
                <TableCell align="center">المستلمون</TableCell>
                <TableCell align="center">النجاح</TableCell>
                <TableCell align="center">الفشل</TableCell>
                <TableCell align="center">التقدم</TableCell>
                <TableCell>الحالة</TableCell>
                <TableCell>التاريخ</TableCell>
                <TableCell align="center">الإجراءات</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={11} align="center">
                    <LinearProgress />
                  </TableCell>
                </TableRow>
              ) : filteredCampaigns.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={11} align="center">
                    <Typography variant="body2" color="text.secondary">
                      لا توجد حملات
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredCampaigns.map((campaign) => (
                  <TableRow key={campaign._id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {campaign.name}
                      </Typography>
                      {campaign.description && (
                        <Typography variant="caption" color="text.secondary">
                          {campaign.description}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {campaign.metadata?.campaignId ? (
                        <Tooltip title="رقم الحملة من المزود">
                          <Chip
                            label={campaign.metadata.campaignId.substring(0, 8) + '...'}
                            size="small"
                            variant="outlined"
                            color="primary"
                          />
                        </Tooltip>
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          -
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={getCampaignTypeText(campaign.campaignType)}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {campaign.templateName || campaign.templateId || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2" fontWeight="medium">
                        {campaign.totalRecipients}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2" color="success.main">
                        {campaign.successCount}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2" color="error.main">
                        {campaign.failedCount}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ minWidth: 100 }}>
                        <LinearProgress
                          variant="determinate"
                          value={campaign.progressPercentage}
                          sx={{ mb: 0.5 }}
                        />
                        <Typography variant="caption">
                          {Math.round(campaign.progressPercentage)}%
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        icon={getStatusIcon(campaign.status)}
                        label={getStatusText(campaign.status)}
                        color={getStatusColor(campaign.status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {campaign.scheduledAt && campaign.status === 'pending' ? (
                        <Tooltip title="موعد الإرسال المجدول">
                          <Box>
                            <ScheduleIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                            <Typography variant="caption">
                              {formatDate(campaign.scheduledAt)}
                            </Typography>
                          </Box>
                        </Tooltip>
                      ) : (
                        <Typography variant="caption">
                          {formatDate(campaign.createdAt)}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <Stack direction="row" spacing={1} justifyContent="center">
                        {canViewDetails && (
                          <Tooltip title="عرض التفاصيل">
                            <IconButton
                              size="small"
                              onClick={() => handleViewDetails(campaign)}
                              color="primary"
                            >
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {canCancel && (campaign.status === 'pending' || campaign.status === 'processing') && (
                          <Tooltip title="إلغاء الحملة">
                            <IconButton
                              size="small"
                              onClick={() => handleCancelCampaign(campaign._id)}
                              color="error"
                            >
                              <CancelIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        {totalPages > 1 && (
          <Box sx={{ p: 2, display: 'flex', justifyContent: 'center' }}>
            <Pagination
              count={totalPages}
              page={page}
              onChange={(_, value) => setPage(value)}
              color="primary"
            />
          </Box>
        )}
      </Card>

      {/* Details Dialog */}
      <Dialog
        open={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          تفاصيل الحملة
        </DialogTitle>
        <DialogContent>
          {selectedCampaign && (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  {selectedCampaign.name}
                </Typography>
                {selectedCampaign.description && (
                  <Typography variant="body2" color="text.secondary" paragraph>
                    {selectedCampaign.description}
                  </Typography>
                )}
              </Grid>

              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">
                  رقم الحملة (المزود)
                </Typography>
                <Typography variant="body2">
                  {selectedCampaign.metadata?.campaignId || '-'}
                </Typography>
              </Grid>

              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">
                  نوع الحملة
                </Typography>
                <Typography variant="body2">
                  {getCampaignTypeText(selectedCampaign.campaignType)}
                </Typography>
              </Grid>

              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">
                  القالب
                </Typography>
                <Typography variant="body2">
                  {selectedCampaign.templateName || selectedCampaign.templateId || '-'}
                </Typography>
              </Grid>

              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">
                  المزود
                </Typography>
                <Typography variant="body2">
                  {selectedCampaign.providerSlug}
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
                  إحصائيات الإرسال
                </Typography>
              </Grid>

              <Grid item xs={3}>
                <Typography variant="caption" color="text.secondary">
                  إجمالي المستلمين
                </Typography>
                <Typography variant="h6">
                  {selectedCampaign.totalRecipients}
                </Typography>
              </Grid>

              <Grid item xs={3}>
                <Typography variant="caption" color="text.secondary">
                  نجح
                </Typography>
                <Typography variant="h6" color="success.main">
                  {selectedCampaign.successCount}
                </Typography>
              </Grid>

              <Grid item xs={3}>
                <Typography variant="caption" color="text.secondary">
                  فشل
                </Typography>
                <Typography variant="h6" color="error.main">
                  {selectedCampaign.failedCount}
                </Typography>
              </Grid>

              <Grid item xs={3}>
                <Typography variant="caption" color="text.secondary">
                  معلق
                </Typography>
                <Typography variant="h6" color="warning.main">
                  {selectedCampaign.pendingCount}
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary">
                  التقدم
                </Typography>
                <Box sx={{ mt: 1 }}>
                  <LinearProgress
                    variant="determinate"
                    value={selectedCampaign.progressPercentage}
                    sx={{ height: 10, borderRadius: 5 }}
                  />
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    {Math.round(selectedCampaign.progressPercentage)}%
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
                  التواريخ
                </Typography>
              </Grid>

              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">
                  تاريخ الإنشاء
                </Typography>
                <Typography variant="body2">
                  {formatDate(selectedCampaign.createdAt)}
                </Typography>
              </Grid>

              {selectedCampaign.scheduledAt && (
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    موعد الإرسال المجدول
                  </Typography>
                  <Typography variant="body2">
                    {formatDate(selectedCampaign.scheduledAt)}
                  </Typography>
                </Grid>
              )}

              {selectedCampaign.startedAt && (
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    تاريخ البدء
                  </Typography>
                  <Typography variant="body2">
                    {formatDate(selectedCampaign.startedAt)}
                  </Typography>
                </Grid>
              )}

              {selectedCampaign.completedAt && (
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    تاريخ الاكتمال
                  </Typography>
                  <Typography variant="body2">
                    {formatDate(selectedCampaign.completedAt)}
                  </Typography>
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsOpen(false)}>
            إغلاق
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default WhatsAppCampaignsPage;